import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { Gamepad2, Pause, Play, SkipBack, SkipForward, VolumeX, WifiOff } from "lucide-react";
import { playerApiBase } from "./api.js";
import type { PlatformBranding } from "./adminOperations.js";

interface ControllerStatus {
  status: "claimed" | "unclaimed";
  pairingCode: string | null;
  tv: { id: string; name: string; online: boolean } | null;
  controllerEnabled?: boolean;
  media: {
    audio: boolean;
    youtube: { targetId: string; label: string; kind: "single" | "playlist" | "list" } | null;
  } | null;
}
const credentialKey = "qassist.controller.credential";

class ControllerRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function controllerRequest<T>(path: string, credential?: string, body?: unknown): Promise<T> {
  const response = await fetch(`${playerApiBase}${path}`, {
    method: body === undefined ? "GET" : "POST",
    cache: "no-store",
    headers: {
      ...(credential ? { Authorization: `Controller ${credential}` } : {}),
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const payload = (await response.json()) as { data: T | null; error: { message: string } | null };
  if (!response.ok || !payload.data)
    throw new ControllerRequestError(
      payload.error?.message ?? "Controller request failed",
      response.status,
    );
  return payload.data;
}

function Controls({
  title,
  nextLabel,
  onCommand,
}: {
  title: string;
  nextLabel: string;
  onCommand(action: "playPause" | "next" | "previous" | "mute"): void;
}) {
  return (
    <section className="controller-media-card">
      <h2>{title}</h2>
      <div className="controller-buttons">
        <button type="button" onClick={() => onCommand("previous")}>
          <SkipBack />
          <span>Previous</span>
        </button>
        <button type="button" className="controller-play" onClick={() => onCommand("playPause")}>
          <Play />
          <Pause />
          <span>Pause / Play</span>
        </button>
        <button type="button" onClick={() => onCommand("next")}>
          <SkipForward />
          <span>{nextLabel}</span>
        </button>
        <button type="button" onClick={() => onCommand("mute")}>
          <VolumeX />
          <span>Mute</span>
        </button>
      </div>
    </section>
  );
}

export function ControllerPage({ branding }: { branding: PlatformBranding }) {
  const [credential, setCredential] = useState(() => localStorage.getItem(credentialKey));
  const [status, setStatus] = useState<ControllerStatus | null>(null);
  const [error, setError] = useState("");
  const style = {
    "--brand-primary": branding.primaryColor,
    "--brand-secondary": branding.secondaryColor,
  } as CSSProperties;
  const register = useCallback(async () => {
    const result = await controllerRequest<{ credential: string; pairingCode: string }>(
      "/api/v1/controllers/register",
      undefined,
      {},
    );
    localStorage.setItem(credentialKey, result.credential);
    setCredential(result.credential);
    setStatus({ status: "unclaimed", pairingCode: result.pairingCode, tv: null, media: null });
  }, []);
  useEffect(() => {
    if (!credential) {
      void register().catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : "Could not register controller"),
      );
      return;
    }
    let stopped = false;
    const refresh = async (): Promise<void> => {
      try {
        const next = await controllerRequest<ControllerStatus>(
          "/api/v1/controllers/status",
          credential,
        );
        if (!stopped) {
          setStatus(next);
          setError("");
        }
      } catch (reason) {
        if (!stopped && reason instanceof ControllerRequestError && reason.status === 401) {
          localStorage.removeItem(credentialKey);
          setCredential(null);
          setStatus(null);
        } else if (!stopped) {
          setError(reason instanceof Error ? reason.message : "Controller is offline");
        }
      }
    };
    void refresh();
    const interval = window.setInterval(() => void refresh(), 1_000);
    return () => {
      stopped = true;
      window.clearInterval(interval);
    };
  }, [credential, register]);
  const command = (
    target: "audio" | "youtube",
    targetId: string | null,
    action: "playPause" | "next" | "previous" | "mute",
  ): void => {
    if (!credential) return;
    void controllerRequest("/api/v1/controllers/commands", credential, {
      target,
      targetId,
      action,
    }).catch((reason: unknown) =>
      setError(reason instanceof Error ? reason.message : "Command failed"),
    );
  };
  return (
    <main className="controller-page" style={style}>
      <header className="controller-header">
        <span className="controller-logo">
          {branding.logoUrl ? <img src={branding.logoUrl} alt="" /> : <Gamepad2 />}
        </span>
        <div>
          <span className="eyebrow">{branding.name}</span>
          <h1>TV Controller</h1>
        </div>
      </header>
      {status?.status !== "claimed" ? (
        <section className="controller-pairing">
          <h2>Claim this controller</h2>
          <p>
            Open the TV in the client dashboard, select <strong>Claim Controller</strong>, and enter
            this PIN.
          </p>
          <strong
            className="controller-pin"
            aria-label={`Controller PIN ${status?.pairingCode?.split("").join(" ") ?? "loading"}`}
          >
            {status?.pairingCode ?? "••••••"}
          </strong>
        </section>
      ) : (
        <>
          <section className={`controller-tv-status ${status.tv?.online ? "is-online" : ""}`}>
            <span>{status.tv?.online ? "Connected" : "Offline"}</span>
            <strong>{status.tv?.name}</strong>
          </section>
          {!status.controllerEnabled ? (
            <p className="controller-empty">
              <WifiOff /> Controller access is disabled for this TV.
            </p>
          ) : null}
          {status.media?.audio ? (
            <Controls
              title="MP3 List"
              nextLabel="Next track"
              onCommand={(action) => command("audio", null, action)}
            />
          ) : null}
          {status.media?.youtube ? (
            <Controls
              title={status.media.youtube.label}
              nextLabel={status.media.youtube.kind === "single" ? "Next Recommended" : "Next"}
              onCommand={(action) => command("youtube", status.media!.youtube!.targetId, action)}
            />
          ) : null}
          {status.controllerEnabled &&
          status.tv?.online &&
          !status.media?.audio &&
          !status.media?.youtube ? (
            <p className="controller-empty">
              <Gamepad2 /> No controller-enabled media is currently visible.
            </p>
          ) : null}
        </>
      )}
      {error ? (
        <p className="notice error" role="alert">
          {error}
        </p>
      ) : null}
    </main>
  );
}
